// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './modules/auth/AuthContext';
import OfflineBanner from './components/shared/OfflineBanner';
import { startSyncEngine } from './offline/syncEngine';
import { CommunicationProvider } from './modules/communications/context/CommunicationContext';
import ProtectedRoute from './modules/auth/ProtectedRoute';
import AdminRoute from './modules/auth/AdminRoute';
import Login from './modules/login/Login';
import RequestResetPassword from './modules/login/RequestResetPassword';
import ChangePassword from './modules/login/ChangePassword';
import RecoveryPassword from './modules/login/RecoveryPassword';
import CreateUser from './modules/login/CreateUser';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Logout from './modules/auth/Logout';
import SettingsList from './modules/settings/SettingsList';
import SettingsForm from './modules/settings/SettingsForm';

import SupplierCategoriesList from './modules/supplier/SupplierCategoriesList';
import SupplierCategoryForm from './modules/supplier/SupplierCategoryForm';
import SupplierList from './modules/supplier/SupplierList';  // Example supplier list route
import SupplierDetail from './modules/supplier/SupplierDetail';  // Example supplier list route
import SupplierForm from './modules/supplier/SupplierForm';  // Example supplier list route
import ProductList from './modules/products/ProductList';
import ProductForm from './modules/products/ProductForm';

import LeadForm from './modules/leads/LeadForm';
import LeadList from './modules/leads/LeadList';
import LeadPipeline from './modules/leads/LeadPipeline';
import LeadStatusForm from './modules/leads/LeadStatusForm';
import LeadStatusList from './modules/leads/LeadStatusList';
import LeadOriginForm from './modules/leads/LeadOriginForm';
import LeadOriginList from './modules/leads/LeadOriginList';
import RoutePlannerPage from './modules/leads/route-planner/RoutePlannerPage';
import DirectCall from './modules/leads/DirectCall';

import ServiceForm from './modules/services/ServiceForm';
import ServiceList from './modules/services/ServiceList';
import ServiceDetails from './modules/services/ServiceDetails';

import CostItemList from './modules/cost-items/CostItemList';
import CostItemForm from './modules/cost-items/CostItemForm';

import EstimateList from './modules/estimates/EstimateList';
import EstimateForm from './modules/estimates/EstimateForm';
import EstimateDetailForm from './modules/estimates/EstimateDetailForm';
import EstimateDetailList from './modules/estimates/EstimateDetailList';
import EstimateView from './modules/estimates/EstimateView';

import JobDashboard from './modules/jobs/JobDashboard';
import JobDetails from './modules/jobs/JobDetails';
import JobForm from './modules/jobs/JobForm';
import JobsReport from './modules/jobs/JobsReport';
import LeadDetails from './modules/leads/LeadDetails';
import UserForm from './modules/users/UserForm';
import UserList from './modules/users/UserList';

// Communications Module
import {
  CommunicationsDashboard,
  AllCommunicationsPage,
  PlaceholderLeadsPage,
  PlaceholderLeadDetailPage,
  SmsComposer,
  CallModal,
  IncomingCallOverlay,
} from './modules/communications';
import { SoftphoneProvider } from './modules/communications/context/SoftphoneContext';

// Follow-Ups Module
import { TemplateBuilderPage, EmailTemplateBuilderPage, BulkSmsPage, ScheduledMessagesPage } from './modules/follow-ups';

// Call Scheduling Module
import { BookingsPage, BookingDetailPage, AvailabilityPage } from './modules/call-scheduling';

// Routes (saved field-visit routes) Module
import { RoutesListPage, RouteDetailPage } from './modules/routes';

// AI Agent Module
import AIAgentDashboard from './modules/ai-agent/AIAgentDashboard';
import IvrMenuManager from './modules/ai-agent/IvrMenuManager';
import AICallLogs from './modules/ai-agent/AICallLogs';
import ServiceAreaManager from './modules/ai-agent/ServiceAreaManager';
import AIEstimatePrompts from './modules/ai-agent/AIEstimatePrompts';
import RingCascadeManager from './modules/phone/RingCascadeManager';

// Financial Module
import FinancialDashboard from './modules/financial/FinancialDashboard';
import InvoiceList from './modules/financial/InvoiceList';
import InvoiceDetail from './modules/financial/InvoiceDetail';
import BusinessExpenseList from './modules/financial/BusinessExpenseList';

// Employees Module (field workers)
import EmployeeList from './modules/employees/EmployeeList';
import EmployeeForm from './modules/employees/EmployeeForm';

// Time-Tracking Module
import TimeTrackingReport from './modules/time-tracking/TimeTrackingReport';
import ShiftDetail from './modules/time-tracking/ShiftDetail';
import LiveMap from './modules/time-tracking/LiveMap';
import SitesList from './modules/time-tracking/SitesList';
import ErrorLogList from './modules/system/ErrorLogList';
import ErrorLogDetail from './modules/system/ErrorLogDetail';
import SiteForm from './modules/time-tracking/SiteForm';
import StandingJobsList from './modules/time-tracking/StandingJobsList';
import StandingJobForm from './modules/time-tracking/StandingJobForm';
import MyTime from './modules/time-tracking/MyTime';
import MyShiftDetail from './modules/time-tracking/MyShiftDetail';
import PayrollReport from './modules/time-tracking/PayrollReport';
import TimeTrackingSettings from './modules/time-tracking/TimeTrackingSettings';
import MyPay from './modules/time-tracking/MyPay';



const App = () => {
  // Warm the offline cache in the background (leads, SMS, calls, recent recordings).
  useEffect(() => {
    startSyncEngine();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <CommunicationProvider>
          <SoftphoneProvider>
          <OfflineBanner /> {/* Connectivity / last-synced indicator */}
          <SmsComposer /> {/* Global SMS composer */}
          <CallModal /> {/* Global call modal */}
          <IncomingCallOverlay /> {/* Global incoming call overlay */}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            
            <Route path="/request-reset-password" element={<RequestResetPassword />} />
            <Route path="/change-password/:token" element={<ChangePassword />} />
            <Route path="/recovery-password" element={<RecoveryPassword />} />

            {/* Public direct-call URL: credential is the direct_call_key in ?k= */}
            <Route path="/direct-call" element={<DirectCall />} />

            
            {/* Protected routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/create-user" element={<ProtectedRoute><CreateUser /></ProtectedRoute>} />
            
            <Route path="/settings" element={<ProtectedRoute><SettingsList /></ProtectedRoute>} />
            <Route path="/settings/new" element={<ProtectedRoute><SettingsForm /></ProtectedRoute>} />
            <Route path="/settings/edit/:id" element={<ProtectedRoute><SettingsForm /></ProtectedRoute>} />

            {/* Communications Module Routes */}
            <Route path="/communications" element={<ProtectedRoute><CommunicationsDashboard /></ProtectedRoute>} />
            <Route path="/communications/dashboard" element={<ProtectedRoute><CommunicationsDashboard /></ProtectedRoute>} />
            <Route path="/communications/all" element={<ProtectedRoute><AllCommunicationsPage /></ProtectedRoute>} />
            <Route path="/communications/placeholder-leads" element={<ProtectedRoute><PlaceholderLeadsPage /></ProtectedRoute>} />
            <Route path="/communications/placeholder-leads/:leadId" element={<ProtectedRoute><PlaceholderLeadDetailPage /></ProtectedRoute>} />

            {/* Saved Routes Module Routes */}
            <Route path="/routes" element={<ProtectedRoute><RoutesListPage /></ProtectedRoute>} />
            <Route path="/routes/:id" element={<ProtectedRoute><RouteDetailPage /></ProtectedRoute>} />

            {/* Call Scheduling Module Routes */}
            <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
            <Route path="/bookings/:id" element={<ProtectedRoute><BookingDetailPage /></ProtectedRoute>} />
            <Route path="/call-schedule/availability" element={<ProtectedRoute><AvailabilityPage /></ProtectedRoute>} />

            {/* Follow-Ups Module Routes */}
            <Route path="/follow-ups" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
            <Route path="/follow-ups/templates" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
            <Route path="/follow-ups/email-templates" element={<ProtectedRoute><EmailTemplateBuilderPage /></ProtectedRoute>} />
            <Route path="/follow-ups/bulk-sms" element={<ProtectedRoute><BulkSmsPage /></ProtectedRoute>} />
            <Route path="/follow-ups/scheduled" element={<ProtectedRoute><ScheduledMessagesPage /></ProtectedRoute>} />

            {/* Supplier Categories Routes */}
            <Route path="/suppliers/categories" element={<ProtectedRoute><SupplierCategoriesList /></ProtectedRoute>} />
            <Route path="/suppliers/categories/new" element={<ProtectedRoute><SupplierCategoryForm /></ProtectedRoute>} />
            <Route path="/suppliers/categories/edit/:id" element={<ProtectedRoute><SupplierCategoryForm /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><SupplierList /></ProtectedRoute>} />
            <Route path="/suppliers/detail/:id" element={<ProtectedRoute><SupplierDetail /></ProtectedRoute>} />
            <Route path="/suppliers/new" element={<ProtectedRoute><SupplierForm /></ProtectedRoute>} />
            <Route path="/suppliers/edit/:id" element={<ProtectedRoute><SupplierForm /></ProtectedRoute>} />

            <Route path="/suppliers/products" element={<ProtectedRoute><ProductList /></ProtectedRoute>} />
            <Route path="/suppliers/products/new" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
            <Route path="/suppliers/products/edit/:id" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />

            <Route path="/leads" element={<ProtectedRoute><LeadList /></ProtectedRoute>} />
            <Route path="/leads/pipeline" element={<ProtectedRoute><LeadPipeline /></ProtectedRoute>} />
            <Route path="/leads/route-planner" element={<ProtectedRoute><RoutePlannerPage /></ProtectedRoute>} />
            <Route path="/leads/view/:id" element={<ProtectedRoute><LeadDetails /></ProtectedRoute>} />
            <Route path="/leads/new" element={<ProtectedRoute><LeadForm /></ProtectedRoute>} />
            <Route path="/leads/edit/:id" element={<ProtectedRoute><LeadForm /></ProtectedRoute>} />

            <Route path="/leads/status" element={<ProtectedRoute><LeadStatusList /></ProtectedRoute>} />
            <Route path="/leads/status/new" element={<ProtectedRoute><LeadStatusForm /></ProtectedRoute>} />
            <Route path="/leads/status/edit/:id" element={<ProtectedRoute><LeadStatusForm /></ProtectedRoute>} />

            <Route path="/leads/origins" element={<ProtectedRoute><LeadOriginList /></ProtectedRoute>} />
            <Route path="/leads/origins/new" element={<ProtectedRoute><LeadOriginForm /></ProtectedRoute>} />
            <Route path="/leads/origins/edit/:id" element={<ProtectedRoute><LeadOriginForm /></ProtectedRoute>} />

            <Route path="/services" element={<ProtectedRoute><ServiceList /></ProtectedRoute>} />
            <Route path="/services/new" element={<ProtectedRoute><ServiceForm /></ProtectedRoute>} />
            <Route path="/services/edit/:id" element={<ProtectedRoute><ServiceForm /></ProtectedRoute>} />
            <Route path="/services/view/:id" element={<ProtectedRoute><ServiceDetails /></ProtectedRoute>} />

            <Route path="/cost-items" element={<ProtectedRoute><CostItemList /></ProtectedRoute>} />
            <Route path="/cost-items/new" element={<ProtectedRoute><CostItemForm /></ProtectedRoute>} />
            <Route path="/cost-items/edit/:id" element={<ProtectedRoute><CostItemForm /></ProtectedRoute>} />

            <Route path="/estimates/" element={<ProtectedRoute><EstimateList /></ProtectedRoute>} />
            <Route path="/estimates/new" element={<ProtectedRoute><EstimateForm /></ProtectedRoute>} />
            <Route path="/estimates/new/:leadId" element={<ProtectedRoute><EstimateForm /></ProtectedRoute>} />
            <Route path="/estimates/edit/:estimateId" element={<ProtectedRoute><EstimateForm /></ProtectedRoute>} />
            <Route path="/estimates/view/:estimateId" element={<ProtectedRoute><EstimateView /></ProtectedRoute>} />
            
            <Route path="/estimates/detail" element={<ProtectedRoute><EstimateDetailList /></ProtectedRoute>} />
            <Route path="/estimates/detail/new" element={<ProtectedRoute><EstimateDetailForm /></ProtectedRoute>} />
            <Route path="/estimates/detail/edit/:id" element={<ProtectedRoute><EstimateDetailForm /></ProtectedRoute>} />

            <Route path="/jobs" element={<ProtectedRoute><JobDashboard /></ProtectedRoute>} />
            <Route path="/jobs/report" element={<ProtectedRoute><JobsReport /></ProtectedRoute>} />
            <Route path="/jobs/new/:estimate_id" element={<ProtectedRoute><JobForm /></ProtectedRoute>} />
            <Route path="/jobs/new" element={<ProtectedRoute><JobForm /></ProtectedRoute>} />
            <Route path="/jobs/edit/:job_id" element={<ProtectedRoute><JobForm /></ProtectedRoute>} />
            <Route path="/jobs/:job_id" element={<ProtectedRoute><JobDetails /></ProtectedRoute>} />

            {/* AI Agent Module Routes */}
            <Route path="/ai-agent" element={<ProtectedRoute><AIAgentDashboard /></ProtectedRoute>} />
            <Route path="/ai-agent/ivr-menu" element={<ProtectedRoute><IvrMenuManager /></ProtectedRoute>} />
            <Route path="/ai-agent/call-logs" element={<ProtectedRoute><AICallLogs /></ProtectedRoute>} />
            <Route path="/ai-agent/service-areas" element={<ProtectedRoute><ServiceAreaManager /></ProtectedRoute>} />
            <Route path="/ai-agent/estimate-prompts" element={<ProtectedRoute><AIEstimatePrompts /></ProtectedRoute>} />

            {/* Phone Module Routes */}
            <Route path="/phone/ring-cascade" element={<ProtectedRoute><RingCascadeManager /></ProtectedRoute>} />

            {/* Financial Module Routes */}
            <Route path="/financial" element={<ProtectedRoute><FinancialDashboard /></ProtectedRoute>} />
            <Route path="/financial/invoices" element={<ProtectedRoute><InvoiceList /></ProtectedRoute>} />
            <Route path="/financial/invoices/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
            <Route path="/financial/expenses" element={<ProtectedRoute><BusinessExpenseList /></ProtectedRoute>} />

            {/* User Management Routes */}
            <Route path="/users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
            <Route path="/users/new" element={<ProtectedRoute><UserForm /></ProtectedRoute>} />
            <Route path="/users/edit/:id" element={<ProtectedRoute><UserForm /></ProtectedRoute>} />

            {/* Employees (field workers) — admin only */}
            <Route path="/employees" element={<AdminRoute><EmployeeList /></AdminRoute>} />
            <Route path="/employees/new" element={<AdminRoute><EmployeeForm /></AdminRoute>} />
            <Route path="/employees/edit/:id" element={<AdminRoute><EmployeeForm /></AdminRoute>} />

            {/* Time Tracking — admin reports/sites/live */}
            <Route path="/time-tracking" element={<AdminRoute><TimeTrackingReport /></AdminRoute>} />
            <Route path="/time-tracking/payroll" element={<AdminRoute><PayrollReport /></AdminRoute>} />
            <Route path="/time-tracking/settings" element={<AdminRoute><TimeTrackingSettings /></AdminRoute>} />
            <Route path="/time-tracking/live" element={<AdminRoute><LiveMap /></AdminRoute>} />
            <Route path="/time-tracking/sites" element={<AdminRoute><SitesList /></AdminRoute>} />
            <Route path="/time-tracking/sites/new" element={<AdminRoute><SiteForm /></AdminRoute>} />
            <Route path="/time-tracking/sites/edit/:id" element={<AdminRoute><SiteForm /></AdminRoute>} />
            <Route path="/time-tracking/standing-jobs" element={<AdminRoute><StandingJobsList /></AdminRoute>} />
            <Route path="/time-tracking/standing-jobs/new" element={<AdminRoute><StandingJobForm /></AdminRoute>} />
            <Route path="/time-tracking/standing-jobs/edit/:id" element={<AdminRoute><StandingJobForm /></AdminRoute>} />
            <Route path="/time-tracking/shifts/:id" element={<AdminRoute><ShiftDetail /></AdminRoute>} />

            {/* System Errors — crash & error tracking (mobile app + backend) */}
            <Route path="/system/errors" element={<AdminRoute><ErrorLogList /></AdminRoute>} />
            <Route path="/system/errors/:id" element={<AdminRoute><ErrorLogDetail /></AdminRoute>} />

            {/* Employee self-portal */}
            <Route path="/my-time" element={<ProtectedRoute><MyTime /></ProtectedRoute>} />
            <Route path="/my-time/pay" element={<ProtectedRoute><MyPay /></ProtectedRoute>} />
            <Route path="/my-time/shifts/:id" element={<ProtectedRoute><MyShiftDetail /></ProtectedRoute>} />



            {/* Redirect unknown routes to login */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </SoftphoneProvider>
        </CommunicationProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
